'use client';

import {Button, ButtonProps} from '@udemy-v2/react-core-components';
import {useI18n, LocalizedHtml} from '@udemy-v2/i18n';
import {publishEvent} from '@udemy-v2/event-tracking';
import {BuyNowEvent} from '@/features/course-landing-page/events/events';
import {PaymentMethodImage} from '@/components/payment-method-image';
import {OneClickCheckoutContext} from '@/types/one-click-checkout-context';
import {useCallback} from 'react';

import styles from './one-click-checkout-button.module.scss';

/**
 * Props for OneClickCheckoutButton component
 */
export interface OneClickCheckoutButtonProps extends ButtonProps {
    /**
     * ID of the course for checkout
     */
    courseId: number;
    /**
     * Tracking ID for analytics
     */
    courseTrackingId?: string;
    /**
     * One-click checkout data
     */
    oneClickCheckoutContext: OneClickCheckoutContext;
}

/**
 * OneClickCheckoutButton component displays a button that allows users
 * to proceed directly to checkout with their saved payment method.
 *
 * The button automatically fetches and displays the user's saved payment
 * information and only renders when one-click checkout is available.
 *
 * @param props - Component props
 * @returns One-click checkout button or null if not enabled
 */
export function OneClickCheckoutButton({
    courseId,
    courseTrackingId,
    size = 'large',
    udStyle = 'secondary',
    onClick,
    oneClickCheckoutContext,
    ...buttonProps
}: OneClickCheckoutButtonProps) {
    const {gettext} = useI18n();
    const {isEnabled, cardBrand, cardLast4Digit, checkoutUrl} = oneClickCheckoutContext;

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            // Fire tracking event
            if (courseTrackingId) {
                publishEvent(
                    new BuyNowEvent({
                        buyable: {
                            id: courseId,
                            type: 'course',
                            trackingId: courseTrackingId,
                        },
                    }),
                );
            }

            // Call optional onClick handler
            onClick?.(e);

            // Navigate to checkout
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
            }
        },
        [courseId, courseTrackingId, checkoutUrl, onClick],
    );

    // Don't render if checkout is not enabled
    if (!isEnabled) {
        return null;
    }

    return (
        <Button
            data-purpose="one-click-checkout-button"
            udStyle={udStyle}
            className={styles['one-click-checkout-button']}
            onClick={handleClick}
            size={size}
            {...buttonProps}
        >
            <LocalizedHtml
                html={gettext(
                    'Proceed with <a class="cardBrand"></a> <a class="cardLast4Digit"></a>',
                )}
                interpolate={{
                    cardBrand: (
                        <PaymentMethodImage
                            methodType={cardBrand}
                            className={styles['brand-image']}
                        />
                    ),
                    cardLast4Digit: `**** ${cardLast4Digit}`,
                }}
            />
        </Button>
    );
}
